import { useEffect, useRef, useState } from 'react'
import { Button, EmptyState, ErrorState, Field, Input, Notice, PageSection, Surface, useOptionalLocalization } from '@solum/ui'
import { assuranceOf } from './assurance.js'
import { useAuth } from './react.js'

export interface McpAssurancePreview {
  proposalId: string
  preview: unknown
  expiresAt: string
  status: 'awaiting_step_up' | 'satisfied'
}

export interface McpAssuranceStepUpProps {
  locator: string | null
  load: (locator: string) => Promise<McpAssurancePreview>
  satisfy: (locator: string) => Promise<unknown>
}

const message = (error: unknown) => error instanceof Error && error.message.trim()
  ? error.message : 'Verification could not be completed safely.'

function previewSummary(value: unknown): string {
  if (!value || typeof value !== 'object') return 'A governed Marble action'
  const row = value as Record<string, unknown>
  const operation = typeof row.operation === 'string' ? row.operation : 'change'
  const entity = typeof row.entity === 'string' ? row.entity : 'record'
  const count = typeof row.changeCount === 'number' ? row.changeCount : null
  return `${operation} ${entity}${count === null ? '' : ` (${count} field${count === 1 ? '' : 's'})`}`
}

/** One host-neutral browser ceremony. Console and Portal supply only their
 * caller-bound PostgREST transport; GoTrue remains the factor/session owner. */
export function McpAssuranceStepUp({ locator, load, satisfy }: McpAssuranceStepUpProps) {
  const { client, session } = useAuth()
  const localization = useOptionalLocalization()
  const [preview, setPreview] = useState<McpAssurancePreview | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [recovery, setRecovery] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const inFlight = useRef(false)
  const msg = (key: string, fallback: string, variables?: Readonly<Record<string, unknown>>) => {
    if (localization) return localization.message(key, fallback, variables)
    return Object.entries(variables ?? {}).reduce(
      (text, [name, value]) => text.replaceAll(`{{${name}}}`, String(value)), fallback,
    )
  }
  const copy = {
    title: msg('auth.assurance.title', 'Verify this action'),
    unavailable: msg('auth.assurance.unavailable', 'Verification unavailable'),
    incomplete: msg('auth.assurance.incomplete', 'This verification link is incomplete.'),
    checking: msg('auth.assurance.checking', 'Checking this one-time request…'),
    completeTitle: msg('auth.assurance.complete.title', 'Action verified'),
    complete: msg('auth.assurance.complete', 'Verification complete. Return to your AI assistant, reconnect this Marble MCP server, and retry the unchanged action once.'),
    summary: msg('auth.assurance.summary', 'This does not approve the action or grant new access. It only proves stronger sign-in assurance for the already governed request.'),
    waiting: msg('auth.assurance.waiting', 'Action waiting for verification. It remains unchanged and expires at {{expires}}.', { expires: preview ? new Date(preview.expiresAt).toLocaleString() : '' }),
    requested: msg('auth.assurance.requested', 'Requested action: {{action}}. Marble will re-check its exact arguments, target state, policy and authority before execution.', { action: preview ? previewSummary(preview.preview) : '' }),
    authenticatorCode: msg('auth.assurance.authenticator.code', 'Authenticator code'),
    authenticatorVerify: msg('auth.assurance.authenticator.verify', 'Verify with authenticator'),
    authenticatorMissing: msg('auth.assurance.authenticator.missing', 'Set up an authenticator app before using this verification method.'),
    recoveryCode: msg('auth.security.recovery.verify.code', 'Recovery code'),
    recoveryVerify: msg('auth.assurance.recovery.verify', 'Use a recovery code'),
    failed: msg('auth.assurance.failed', 'Verification failed.'),
  }

  useEffect(() => {
    let live = true
    if (!locator) { setFailure(copy.incomplete); return () => { live = false } }
    void load(locator).then((value) => { if (live) setPreview(value) })
      .catch((error) => { if (live) setFailure(message(error)) })
    return () => { live = false }
  }, [load, locator, copy.incomplete])

  async function finish(kind: 'totp' | 'recovery') {
    if (!locator || inFlight.current) return
    inFlight.current = true; setBusy(true); setFailure(null)
    try {
      if (assuranceOf(session?.accessToken ?? '') !== 'aal2') {
        if (kind === 'totp') {
          const factors = await client.listMfaFactors()
          const factor = factors.find((candidate) => candidate.type === 'totp' && candidate.status === 'verified')
          if (!factor) throw new Error(copy.authenticatorMissing)
          await client.challengeAndVerifyTotp(factor.id, otp)
        } else {
          await client.verifyRecoveryCode(recovery)
        }
      }
      await satisfy(locator)
      setDone(true); setOtp(''); setRecovery('')
    } catch (error) { setFailure(message(error)) }
    finally { inFlight.current = false; setBusy(false) }
  }

  if (failure && !preview) return <PageSection title={copy.title}><ErrorState title={copy.unavailable} message={failure} /></PageSection>
  if (!preview) return <PageSection title={copy.title}><EmptyState>{copy.checking}</EmptyState></PageSection>
  if (done || preview.status === 'satisfied') return (
    <PageSection title={copy.completeTitle}><Notice>{copy.complete}</Notice></PageSection>
  )
  return (
    <PageSection title={copy.title} summary={copy.summary}>
      <Surface>
        <Notice tone="info">{copy.waiting}</Notice>
        <Notice>{copy.requested}</Notice>
        <Field label={copy.authenticatorCode}>
          <Input value={otp} inputMode="numeric" autoComplete="one-time-code" maxLength={8}
            onChange={(event) => setOtp(event.target.value)} disabled={busy} />
        </Field>
        <Button onClick={() => { void finish('totp') }} disabled={busy || otp.trim().length < 6}>{copy.authenticatorVerify}</Button>
        <Field label={copy.recoveryCode}>
          <Input value={recovery} autoComplete="one-time-code"
            onChange={(event) => setRecovery(event.target.value)} disabled={busy} />
        </Field>
        <Button variant="secondary" onClick={() => { void finish('recovery') }} disabled={busy || recovery.trim().length < 8}>{copy.recoveryVerify}</Button>
        {failure ? <Notice tone="critical" live="alert">{copy.failed} {failure}</Notice> : null}
      </Surface>
    </PageSection>
  )
}
